import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * ResizeObserver used to keep --header-drawer-top-offset in sync
   * @type {ResizeObserver | null}
   */
  #drawerOffsetObserver = null;

  // empty-cart CTA tracking
  /** @type {boolean | null} */
  #cartIsEmpty = null;
  /** @type {((event: CustomEvent<{ data?: { itemCount?: number; source?: string } }>) => void) | null} */
  #cartUpdateHandler = null;
  /** @type {HTMLElement | null} */
  #cartDrawerComponent = null;
  /** @type {(() => void) | null} */
  #cartDrawerCloseHandler = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';

        // Keep an explicit baseline state so scroll-up sticky selectors can apply
        // sticky positioning before the header ever becomes active.
        if (isIntersecting && this.dataset.stickyState !== 'active') {
          this.dataset.stickyState = 'inactive';
          this.dataset.scrollDirection = 'none';
        }
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    this.#updateTransparentBoundaryState();

    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';

      this.dataset.stickyState = 'idle';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  #updateTransparentBoundaryState = () => {
    if (!this.hasAttribute('transparent') || this.getAttribute('transparent-until') !== 'first-section') {
      delete this.dataset.transparentPastFirstSection;
      return;
    }

    // When a pixel-offset threshold is set, use scroll position instead of first-section boundary
    if (this.dataset.transparentScrollPx !== undefined) {
      const threshold = parseInt(this.dataset.transparentScrollPx, 10);
      const scrollTop = document.scrollingElement?.scrollTop ?? 0;
      this.dataset.transparentPastFirstSection = scrollTop >= threshold ? 'true' : 'false';
      return;
    }

    const firstSection = document.querySelector('#MainContent .shopify-section');
    if (!(firstSection instanceof HTMLElement)) {
      this.dataset.transparentPastFirstSection = 'true';
      return;
    }

    const headerBottom = this.getBoundingClientRect().bottom;
    const firstSectionBottom = firstSection.getBoundingClientRect().bottom;
    const pastFirstSection = firstSectionBottom <= headerBottom;
    this.dataset.transparentPastFirstSection = pastFirstSection ? 'true' : 'false';
  };

  // ----- cart CTA helper -------------------------------------------------
  /**
   * CUSTOM: Sets up a ResizeObserver to dynamically measure header__row--top height
   * and inject it into the --header-drawer-top-offset CSS variable.
   * 
   * This replaces the hardcoded --custom-mobile-drawer-top-offset approach.
   * Benefits:
   * - Drawer positioning is always precise, regardless of header height changes
   * - Adapts to responsive breakpoints, font loading, content reflow
   * - Pure CSS-based positioning via custom properties (no JavaScript positioning needed after initial setup)
   * 
   * Called during connectedCallback() initialization.
   * Only activates when a drawer with 'header__drawer--custom-animation-true' exists.
   */
  #setupDrawerOffsetObserver = () => {
    const customAnimatedDrawer = this.querySelector('.header__drawer--custom-animation-true');
    if (!(customAnimatedDrawer instanceof HTMLElement)) return;

    const headerRowTop = this.querySelector('.header__row--top');
    if (!(headerRowTop instanceof HTMLElement)) return;

    this.#drawerOffsetObserver?.disconnect();
    this.#drawerOffsetObserver = new ResizeObserver(() => {
      const topRowHeight = Math.round(headerRowTop.offsetHeight);
      this.style.setProperty('--header-drawer-top-offset', `${topRowHeight}px`);
    });

    // Set immediately, then keep in sync as layout changes.
    const topRowHeight = Math.round(headerRowTop.offsetHeight);
    this.style.setProperty('--header-drawer-top-offset', `${topRowHeight}px`);
    this.#drawerOffsetObserver.observe(headerRowTop);
  };

  #initHeaderCtaToggle() {
    const headerActions = this.querySelector('header-actions');
    if (!headerActions) return;

    const ctaEl = headerActions.querySelector('[data-empty-cart-cta]');
    const cartEls = headerActions.querySelectorAll('[data-cart-icon-block]');
    if (!(ctaEl instanceof HTMLElement) || !cartEls.length) return;

    const ctaMode = ctaEl.dataset.emptyCartCtaMode ?? 'empty';

    // In "always" mode, server-rendered visibility is already correct and does not depend on cart updates.
    if (ctaMode === 'always') return;

    // initial state inferred from server render (CTA visible when empty)
    this.#cartIsEmpty = !ctaEl.hidden;

    this.#cartDrawerComponent = headerActions.querySelector('cart-drawer-component');

    const syncCartVisibility = () => {
      const drawerDialog = this.#cartDrawerComponent?.querySelector('dialog[ref="dialog"]');
      const isDrawerOpen = drawerDialog instanceof HTMLDialogElement && drawerDialog.open;
      const keepCartVisible = this.#cartIsEmpty && isDrawerOpen;

      if (this.#cartIsEmpty && !keepCartVisible) {
        ctaEl.removeAttribute('hidden');
        cartEls.forEach((el) => el.setAttribute('hidden', ''));
      } else {
        ctaEl.setAttribute('hidden', '');
        cartEls.forEach((el) => el.removeAttribute('hidden'));
      }
    };

    if (this.#cartDrawerComponent) {
      this.#cartDrawerCloseHandler = () => {
        if (this.#cartIsEmpty) {
          syncCartVisibility();
        }
      };
      this.#cartDrawerComponent.addEventListener('dialog:close', this.#cartDrawerCloseHandler);
    }

    this.#cartUpdateHandler = (event) => {
      const itemCount = event.detail?.data?.itemCount ?? 0;
      const comingFromProductForm = event.detail?.data?.source === 'product-form-component';
      const newCartIsEmpty = comingFromProductForm ? false : itemCount === 0;
      if (newCartIsEmpty === this.#cartIsEmpty) return;
      this.#cartIsEmpty = newCartIsEmpty;

      syncCartVisibility();
    };

    document.addEventListener(ThemeEvents.cartUpdate, this.#cartUpdateHandler);
  }

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.#setupDrawerOffsetObserver();
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);
    this.#updateTransparentBoundaryState();

    // initialize empty-cart CTA logic (moved from header-cta.js)
    this.#initHeaderCtaToggle();

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode || this.getAttribute('transparent-until') === 'first-section') {
      if (stickyMode) {
        this.#observeStickyPosition(stickyMode === 'always');
      }

      if (stickyMode === 'scroll-up' || stickyMode === 'always' || this.getAttribute('transparent-until') === 'first-section') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }

    window.addEventListener('resize', this.#handleWindowScroll);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#drawerOffsetObserver?.disconnect();
    this.#drawerOffsetObserver = null;
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    window.removeEventListener('resize', this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');

    // cleanup cart listener
    if (this.#cartUpdateHandler) {
      document.removeEventListener(ThemeEvents.cartUpdate, this.#cartUpdateHandler);
      this.#cartUpdateHandler = null;
    }
    if (this.#cartDrawerComponent && this.#cartDrawerCloseHandler) {
      this.#cartDrawerComponent.removeEventListener('dialog:close', this.#cartDrawerCloseHandler);
      this.#cartDrawerCloseHandler = null;
    }
    this.#cartDrawerComponent = null;
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
